import axios from 'axios'
import { getApiBaseUrl } from './apiBase'
import { useDateFormat } from '../../../stores/dateFormat';
export function getBaseUrl() {
    return getApiBaseUrl();
}

let loginWindow = null;
let reAuthPromise = null;

function waitForReAuth() {
    if (reAuthPromise) return reAuthPromise;

    reAuthPromise = new Promise((resolve) => {
        if (!loginWindow || loginWindow.closed) {
            // Login lives on the app's own origin, also when requests go to the admin setup mirror.
            loginWindow = window.open('/login?autoclose=1', '_blank')
        } else {
            loginWindow.focus();
        }

        const poll = setInterval(() => {
            if (!loginWindow || loginWindow.closed) {
                clearInterval(poll);
                loginWindow = null;
                reAuthPromise = null;
                resolve();
            }
        }, 500);
    });

    return reAuthPromise;
}

let dataRefreshPromise = null;
const DATA_REFRESH_POLL_MS = 2000;
const DATA_REFRESH_GIVE_UP_MS = 12 * 60 * 1000;

// The server lost this session's customer data. Calls that notice it at the same time share one
// refresh from HubSpot instead of starting one each.
function waitForDataRefresh() {
    if (!dataRefreshPromise) {
        dataRefreshPromise = (async () => {
            const started = (await fetchData(`${getBaseUrl()}/login/data-update`, { method: 'GET' })).data;
            if (started.result !== 'started') {
                throw new Error(`Data refresh did not start: ${started.result}`);
            }
            const giveUpAt = Date.now() + DATA_REFRESH_GIVE_UP_MS;
            while (Date.now() < giveUpAt) {
                await new Promise((resolve) => setTimeout(resolve, DATA_REFRESH_POLL_MS));
                const url = `${getBaseUrl()}/login/data-update/status?startedAt=${started.startedAt}`;
                const status = (await fetchData(url, { method: 'GET' })).data;
                if (status.result === 'ok') {
                    return status;
                }
                if (status.result !== 'refreshing') {
                    throw new Error(status.message ?? `Data refresh ${status.result}`);
                }
            }
            throw new Error('Data refresh took too long');
        })().finally(() => { dataRefreshPromise = null; });
    }
    return dataRefreshPromise;
}

async function fetchData(url, request, reAuthed = false, refreshed = false) {
    try {
        request['withCredentials'] = true
        const response = await axios(url, request)
        return response
    } catch (error) {
        if (error.response && error.response.status === 401 && !reAuthed) {
            await waitForReAuth();
            return fetchData(url, request, true, refreshed);
        } else if (error.response && error.response.status === 409 && error.response.data?.result === 'refresh' && !refreshed) {
            await waitForDataRefresh();
            return fetchData(url, request, reAuthed, true);
        } else {
            throw error
        }
    }
}

export const updateDataFromHubspot = () => waitForDataRefresh()

export const fetchMe = async () => {
    const url = `${getBaseUrl()}/me`
    const request = { method: 'GET' }
    const response = await fetchData(url, request)
    useDateFormat().setDateFormat(response.data?.organization?.dateFormat)
    return response.data
}

export const fetchImpactDrivers = async (includedHidden = false) => {
    const url = `${getBaseUrl()}/api/values/impact-drivers?hidden=${includedHidden}`
    const request = { method: 'GET' }
    const response = await fetchData(url, request)

    return response.data
}

export const fetchImpactDriverStatuses = async () => {
    const url = `${getBaseUrl()}/api/values/impact-driver-statuses`
    const request = { method: 'GET' }
    const response = await fetchData(url, request)

    return response.data
}

export const fetchCompanies = async (extraColumns = null, forceReimport = false, mode = null) => {
    let url = `${getBaseUrl()}/api/customers`;

    if (forceReimport) {
        await waitForDataRefresh();
    }
    const params = [];
    if (extraColumns != null) {
        params.push(`extra_props=${extraColumns.join(',')}`);
    }
    if (mode) {
        params.push(`mode=${mode}`);
    }
    if (params.length > 0) {
        url += `?${params.join('&')}`;
    }

    const request = { method: 'GET' };
    const response = await fetchData(url, request);
    return response.data;
}

export const fetchWidgets = async () => {
    const url = `${getBaseUrl()}/api/dashboard/widgets`
    const request = { method: 'GET' }
    const response = await fetchData(url, request)

    return response.data
}

export const fetchAccountPlans = async (includeArchived = false) => {
    let url = `${getBaseUrl()}/api/account-plans`;
    if (includeArchived) {
        url += '?includeArchived=true';
    }

    const request = { method: 'GET' }
    const response = await fetchData(url, request)

    return response.data
}

export const accountPlanDelete = async (accountPlan) => {
    const url = `${getBaseUrl()}/api/account-plans`
    const request = {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json', },
        data: JSON.stringify([accountPlan.id])
    }
    try {
        const response = await fetchData(url, request)
        if (response.statusText != 'OK') {
            throw new Error(`Server error while performing the operation.`);
        }
    } catch (error) {
        toastr.error(error)
    }
}

export const bulkEditAccountPlans = async (apIds, action) => {
    const url = `${getBaseUrl()}/api/account-plans/bulk/${action}`
    const request = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', },
        data: JSON.stringify(apIds),
    }
    try {
        const response = await fetchData(url, request)
        if (response.statusText != 'OK') {
            throw new Error(`Server error while performing the operation.`);
        }
        return response.data
    } catch (error) {
        toastr.error("Unable to update selected account plans")
    }
}

export const sendAccountPlans = async (accountPlans) => {
    const url = `${getBaseUrl()}/api/account-plans`
    const request = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', },
        data: JSON.stringify(accountPlans),
    }
    try {
        const response = await fetchData(url, request)
        if (response.statusText != 'OK') {
            throw new Error(`Server error while performing the operation.`);
        }
        return response.data
    } catch (error) {
        toastr.error("Unable to save account plan")
    }

}

export const recoverImpactDriver = async (impactDriverId) => {
    const url = `${getBaseUrl()}/api/impact-drivers/recover/${impactDriverId}`;
    const request = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
    }
    try {
        const response = await fetchData(url, request)
        if (response.statusText != 'OK') {
            throw new Error(`Server error while performing the operation.`);
        }
        return response.data
    } catch (error) {
        toastr.error("Unable to recover impact driver")
    }

}

export const updateAccountPlanPublishStatus = async (newStatus, accountPlan) => {
    const url = `${getBaseUrl()}/api/account-plan/${accountPlan.id}/publish${newStatus == false ? '?unpublish' : ''}`
    const request = { method: 'GET' }

    try {
        const response = await fetchData(url, request)
        if (response.statusText != 'OK') {
            throw new Error(`Server error while performing the operation.`);
        }
        // Show warning if present (note creation failed but publish succeeded)
        if (response.data.warning) {
            toastr.warning(response.data.warning);
        }
        return response.data
    } catch (error) {
        toastr.error("Unable to save account plan");
        throw error
    }

}

export const fetchCompanyEngagments = async (companyId) => {
    const url = `${getBaseUrl()}/api/customer/${companyId}/engagements`
    const request = { method: 'GET' }

    try {
        const response = await fetchData(url, request)
        if (response.statusText != 'OK') {
            throw new Error(`Server error while performing the operation.`);
        }
        return response.data
    } catch (error) {
        toastr.error("Unable to save account plan");
        throw error
    }

}

export const updateCompanyImpactDriverStatus = async (companyId, impactDriverId, newStatusId, engagement = null, noteText = null) => {
    const engagementQueryParam = engagement != null && engagement != undefined && engagement != 0
        ? `?engagement=${engagement.id}&engagementType=${engagement.type}`
        : '';
    const url = `${getBaseUrl()}/api/customer/${companyId}/impact-driver/${impactDriverId}/${newStatusId}${engagementQueryParam}`

    const body = {};
    if (noteText) body.note = noteText;

    const request = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', },
        data: JSON.stringify(body)
    }

    try {
        let response = await fetchData(url, request)
        if (response.statusText != 'OK') {
            throw new Error(`Server error while performing the operation.`)
        }
        // Show warning if present (status was saved but some properties could not be synced)
        if (response.data.warning) {
            toastr.warning(response.data.warning);
        }
        return response.data
    } catch (error) {
        toastr.error("Unable to save changes");
        throw error
    }
}

export const fetchProperties = async () => {
    const url = `${getBaseUrl()}/api/properties/company`
    const request = { method: 'GET' }
    const response = await fetchData(url, request)

    return response.data
}

export const fetchFilters = async () => {
    const url = `${getBaseUrl()}/api/filters`
    const request = { method: 'GET' }
    const response = await fetchData(url, request)

    return response.data
}

export const sendFilter = async (filter) => {
    const url = filter.id ? `${getBaseUrl()}/api/filter/${filter.id}` : `${getBaseUrl()}/api/filters`
    const request = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', },
        data: JSON.stringify(filter)
    }
    const response = await fetchData(url, request)
    return response.data
}

export const updateFilterWidgetLayout = async (filterId, widgetLayout) => {
    const url = `${getBaseUrl()}/api/filter/${filterId}/widget-layout`
    const request = {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        data: JSON.stringify({ widgetLayout })
    }
    const response = await fetchData(url, request)
    return response.data
}

export const deleteFilter = async (id) => {
    const url = `${getBaseUrl()}/api/filter/${id}`
    const request = { method: 'DELETE' }
    const response = await fetchData(url, request)

    return response.data
}

export const saveTemplate = async (data) => {
    const url = `${getBaseUrl()}/api/settings`
    const request = {
        method: 'POST',
        data: JSON.stringify(data)
    }
    const response = await fetchData(url, request)

    return response.data
}

export const fetchUsersSettings = async () => {
    const url = `${getBaseUrl()}/settings/users`
    const request = { method: 'GET' }
    const response = await fetchData(url, request)

    return response.data
}

export const sendUserSettingsData = async (user) => {
    const url = `${getBaseUrl()}/settings/users/${user.id}`
    const request = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', },
        data: JSON.stringify(user)
    }
    const response = await fetchData(url, request)

    return response.data
}

export const deleteUserRequest = async (user) => {
    const url = `${getBaseUrl()}/settings/users/${user.id}`
    const request = {
        method: 'DELETE',
    }
    const response = await fetchData(url, request)

    return response.data
}

export const fetchCompanyOwnerFields = async () => {
    const url = `${getBaseUrl()}/api/values/owner-fields`
    const request = { method: 'GET' }
    const response = await fetchData(url, request)

    return response.data
}

export const getGlobalSettings = async () => {
    const url = `${getBaseUrl()}/settings/list`
    const request = { method: 'GET' }
    let response;
    try {
        response = await fetchData(url, request)
    } catch (error) {
        // try again
        response = await fetchData(url, request)
    }

    return response.data
}

export const updateGeneralSettings = async (data, noDataReimport = false) => {
    const url = `${getBaseUrl()}/settings/update?noreimport=true`
    const request = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', },
        data: JSON.stringify(data)
    }
    const response = await fetchData(url, request)
    if (!noDataReimport) {
        await waitForDataRefresh()
    }

    return response.data
}

export const updateLeaderboardSorting = async (sorting) => {
    const url = `${getBaseUrl()}/api/settings`
    const request = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: JSON.stringify({ leaderboard_sorting: sorting })
    }
    const response = await fetchData(url, request)
    return response.data
}

export const fetchCycle = async () => {
    const url = `${getBaseUrl()}/api/cycle`
    const request = { method: 'GET' }

    try {
        const response = await fetchData(url, request)
        if (response) {
            return response.data
        }
    } catch (error) {
        if (error.response && error.response.status !== 404) {
            throw error
        }
    }
}

export const fetchCycleStats = async ({ owners = [], customers = [], segments = [], period = null, statuses = [], cycleType = 'active' } = {}) => {
    const params = new URLSearchParams();

    owners.forEach(id => params.append('owners[]', id));
    customers.forEach(id => params.append('customers[]', id));
    segments.forEach(segment => params.append('segments[]', segment));
    if (period) period.forEach(date => params.append('period[]', date));
    statuses.forEach(status => params.append('statuses[]', status));

    if (cycleType) params.append('cycleType', cycleType);

    const url = `${getBaseUrl()}/api/cycle/stats?${params.toString()}`;
    const request = { method: 'GET' };

    try {
        const response = await fetchData(url, request);
        return response?.data || null;
    } catch (error) {
        if (error.response && error.response.status !== 404) {
            throw error;
        }
        return null;
    }
};


export const fetchPreviousCycle = async () => {
    const url = `${getBaseUrl()}/api/cycle/previous`
    const request = { method: 'GET' }

    try {
        const response = await fetchData(url, request)
        if (response) {
            return response.data
        }
    } catch (error) {
        if (error.response && error.response.status !== 404) {
            throw error
        }
    }
}

export const approveCycle = async () => {
    const url = `${getBaseUrl()}/api/cycle/approve`
    const request = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', }
    }
    const response = await fetchData(url, request)

    return response.data
}

export const updateCycle = async (data) => {
    const url = `${getBaseUrl()}/api/cycle`
    const request = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', },
        data: JSON.stringify(data)
    }
    const response = await fetchData(url, request)

    return response.data
}

export const getEndTheCycleProgress = async () => {
    const url = `${getBaseUrl()}/api/cycle/job/progress`
    const request = { method: 'GET' }
    const response = await fetchData(url, request)

    return response.data
}

export const upsertImpactDriver = async (data) => {
    const url = `${getBaseUrl()}/api/impact-drivers/upsert`
    const request = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', },
        data: JSON.stringify(data)
    }
    const response = await fetchData(url, request)

    return response.data
}

export const deleteImpactDriver = async (id, force = false) => {
    const url = `${getBaseUrl()}/api/impact-drivers/delete/${id}${force ? '?force=true' : ''}`
    const request = { method: 'POST' }
    const response = await fetchData(url, request)

    return response.data
}

export async function getPointsLog(params) {
    const queryParams = new URLSearchParams();
    
    if (params.page !== undefined) {
        queryParams.append('page', params.page);
    }
    if (params.limit !== undefined) {
        queryParams.append('limit', params.limit);
    }

    if (params.sortBy) {
        queryParams.append('sortBy', params.sortBy);
    }
    if (params.sortOrder) {
        queryParams.append('sortOrder', params.sortOrder);
    }
    if (params.filters?.owner?.length) {
        queryParams.append('owner', params.filters.owner.join(','));
    }
    if (params.filters?.changedBy?.length) {
        queryParams.append('changedBy', params.filters.changedBy.join(','));
    }
    const request = { method: 'GET' }
    const url = `${getBaseUrl()}/api/pointslog?${queryParams}`

    const response = await fetchData(url, request);

    return await response.data;
}

export const pointsLogReset = async (id) => {
    const url = `${getBaseUrl()}/api/pointslog/${id}/reset`
    const request = { method: 'PATCH' }
    const response = await fetchData(url, request)

    return response.data
}

export const getLibraryImpactDrivers = async () => {
    const url = `${getBaseUrl()}/api/values/library/impact-drivers`
    const request = { method: 'GET' }
    const response = await fetchData(url, request)

    return response.data
}

export const getCalculatedProperties = async () => {
    const url = `${getBaseUrl()}/api/calculatedproperties`
    const request = { method: 'GET' }
    const response = await fetchData(url, request)

    return response.data
}

export const getCalculatedPropertyOptions = async () => {
    const url = `${getBaseUrl()}/api/calculatedproperties/options`
    const request = { method: 'GET' }
    const response = await fetchData(url, request)

    return response.data
}

export const enableCalculatedProperty = async (propName) => {
    const url = `${getBaseUrl()}/api/calculatedproperties/enable/${propName}`
    const request = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', }
    }
    const response = await fetchData(url, request)

    return response.data
}

export const syncAllProperties = async () => {
    const url = `${getBaseUrl()}/api/calculatedproperties/sync`
    const request = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', }
    }
    const response = await fetchData(url, request)

    return response.data
}

export const getPropertySyncStatus = async (type) => {
    const params = type ? `?type=${encodeURIComponent(type)}` : ''
    const url = `${getBaseUrl()}/api/calculatedproperties/sync/status${params}`
    const request = { method: 'GET' }
    const response = await fetchData(url, request)

    return response.data
}

export const disableCalculatedProperty = async (propId) => {
    const url = `${getBaseUrl()}/api/calculatedproperties/${propId}/disable`
    const request = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', }
    }
    const response = await fetchData(url, request)

    return response.data
}

export const createCustomCalculatedProperty = async (data) => {
    const url = `${getBaseUrl()}/api/calculatedproperties/custom`
    const request = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: data,
    }
    const response = await fetchData(url, request)
    return response.data
}

export const updateCustomCalculatedProperty = async (propertyId, data) => {
    const url = `${getBaseUrl()}/api/calculatedproperties/custom/${propertyId}`
    const request = {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        data: data,
    }
    const response = await fetchData(url, request)
    return response.data
}

export const deleteCustomCalculatedProperty = async (propertyId) => {
    const url = `${getBaseUrl()}/api/calculatedproperties/custom/${propertyId}`
    const request = {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
    }
    const response = await fetchData(url, request)
    return response.data
}

export const fetchCalculationTemplates = async () => {
    const url = `${getBaseUrl()}/api/detector-templates?type=calculation`
    const request = { method: 'GET' }
    const response = await fetchData(url, request)
    return response.data
}

export const validateExpression = async (expression) => {
    const url = `${getBaseUrl()}/api/calculatedproperties/custom/validate`
    const request = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: { expression },
    }
    const response = await fetchData(url, request)
    return response.data
}

export const isFeatureEnabled = async (name) => {
    let url = `${getBaseUrl()}/api/feature-enabled/${name}`
    const request = { method: 'GET' }
    const response = await fetchData(url, request)

    return response.data && response.data.result && response.data.result === true
}

export const sendChatMessage = async (message, { customerCrmId = null, scope = null, history = null } = {}) => {
    const url = `${getBaseUrl()}/api/chat`

    const body = { message }
    if (customerCrmId) body.customer_crm_id = customerCrmId
    if (scope) body.scope = scope
    if (history) body.history = history

    return fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
    })
}

export const fetchChatHistory = async (customerCrmId) => {
    const url = `${getBaseUrl()}/api/chat/history/${encodeURIComponent(customerCrmId)}`
    const response = await fetchData(url, { method: 'GET' })
    return response.data
}

export const fetchGlobalChatHistory = async () => {
    const url = `${getBaseUrl()}/api/chat/history/global`
    const response = await fetchData(url, { method: 'GET' })
    return response.data
}

export const clearChatHistory = async (customerCrmId) => {
    const url = `${getBaseUrl()}/api/chat/clear`
    const response = await fetchData(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: JSON.stringify({ customer_crm_id: customerCrmId }),
    })
    return response.data
}

export const clearGlobalChat = async () => {
    const url = `${getBaseUrl()}/api/chat/clear`
    const response = await fetchData(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: JSON.stringify({ scope: 'global' }),
    })
    return response.data
}

export const publishReportAsNote = async (reportText, customerCRMId) => {
    const url = `${getBaseUrl()}/api/chat/actions/save-note`
    const request = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', },
        data: JSON.stringify({
            'content': reportText,
            'customer_crm_id': customerCRMId
        })
    }
    const response = await fetchData(url, request)

    return response.data
}

export const getNotificationSettings = async () => {
    const url = `${getBaseUrl()}/notifications/settings`
    const request = { method: 'GET' }
    const response = await fetchData(url, request)

    return response.data
}

export const saveNotificationSettings = async (settings) => {
    const url = `${getBaseUrl()}/notifications/settings`
    const request = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: JSON.stringify(settings)
    }
    const response = await fetchData(url, request)

    return response.data
}

export const fetchTransitionData = async (impactDriverId, companyCRMId, companyImpactDriverStatusId) => {
    const url = `${getBaseUrl()}/api/impact-drivers/${impactDriverId}/customer/${companyCRMId}/overview/${companyImpactDriverStatusId}`
    const request = { method: 'GET' }
    const response = await fetchData(url, request)
    return response.data
}

export const fetchTransitionSuggestions = async () => {
    const url = `${getBaseUrl()}/api/ts`
    const request = { method: 'GET' }
    const response = await fetchData(url, request)
    return response.data
}

export const actOnTransitionSuggestions = async (action, uuid, rejectionReason) => {
    if (!['accept', 'reject', 'snooze'].includes(action)) {
        return false;
    }

    const url = `${getBaseUrl()}/api/ts/${action}/${uuid}`;
    const requestData = action === 'reject' && rejectionReason
        ? { rejectionReason }
        : undefined;

    const request = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: requestData ? JSON.stringify(requestData) : undefined
    };

    const response = await fetchData(url, request);
    return response.data;
}

export const actOnSignalSuggestion = async (action, signalId, rejectionReason) => {
    if (!['accept', 'reject', 'snooze'].includes(action)) {
        return false;
    }

    const url = `${getBaseUrl()}/api/ts/signal/${signalId}/${action}`;
    const requestData = action === 'reject' && rejectionReason
        ? { rejectionReason }
        : undefined;

    const request = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: requestData ? JSON.stringify(requestData) : undefined
    };

    const response = await fetchData(url, request);
    return response.data;
}

export const refetchDataOfOneCompany = async (companyCrmId) => {
    const url = `${getBaseUrl()}/login/data-update-company/${companyCrmId}`
    const request = { method: 'POST' }
    const response = await fetchData(url, request)
    return response.data
}

export const updateCustomerPropertyValue = async (propertyName, customerId, newValue, noteText) => {
    const url = `${getBaseUrl()}/api/properties/update/${propertyName}/${customerId}`
    const request = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', },
        data: JSON.stringify({ value: newValue, noteText: noteText }),
    }
    const response = await fetchData(url, request)
    return response.data
}

export const updateCustomerPropertyConditionalFormatting = async (propertyName, formatting) => {
    const url = `${getBaseUrl()}/api/properties/update-conditional-formatting/${propertyName}`
    const request = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', },
        data: JSON.stringify({ formatting: formatting }),
    }
    const response = await fetchData(url, request)
    return response.data
}

export async function saveImpactDriverStatus(status) {
    const url = `${getBaseUrl()}/settings/status`
    const request = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', },
        data: JSON.stringify(status),
    }
    const response = await fetchData(url, request)
    return response.data
}

export async function getImpactDriverStatusUsages(statusId) {
    const url = `${getBaseUrl()}/settings/status/${statusId}/usages`
    const request = { method: 'GET' }
    const response = await fetchData(url, request)
    return response.data
}

export const fetchImpactAreas = async () => {
    const url = `${getBaseUrl()}/settings/impactarea`
    const request = { method: 'GET' }
    const response = await fetchData(url, request)

    return response.data
}

export async function saveImpactArea(area) {
    const url = `${getBaseUrl()}/settings/impactarea`
    const request = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', },
        data: JSON.stringify(area),
    }
    const response = await fetchData(url, request)
    return response.data
}

export async function deleteImpactArea(area) {
    const url = `${getBaseUrl()}/settings/impactarea/${area.id}`
    const request = {
        method: 'DELETE'
    }
    const response = await fetchData(url, request)
    return response.data
}

export async function fetchKeymetrics() {
    const url = `${getBaseUrl()}/settings/keymetrics`
    const request = { method: 'GET' }
    const response = await fetchData(url, request)
    return response.data

}

export async function fetchInsightTemplates() {
    const url = `${getBaseUrl()}/settings/insights/templates`
    const request = { method: 'GET' }
    const response = await fetchData(url, request)
    return response.data
}

export async function fetchInsights() {
    const url = `${getBaseUrl()}/settings/insights`
    const request = { method: 'GET' }
    const response = await fetchData(url, request)
    return response.data
}

export async function upsertInsight(data) {
    const url = `${getBaseUrl()}/settings/insights`
    const request = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', },
        data: JSON.stringify(data),
    }
    const response = await fetchData(url, request)
    return response.data
}
export const deleteInsight = async (insight) => {
    const url = `${getBaseUrl()}/settings/insights`
    const request = {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json', },
        data: JSON.stringify({ id: insight.id }),
    }
    const response = await fetchData(url, request)
    return response.data
}

export async function fetchInsightFindings(params) {
    const url = `${getBaseUrl()}/api/insights/findings`;
    const request = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: JSON.stringify(params),
    };
    const response = await fetchData(url, request);
    return response.data;
}

export async function fetchInsightStats(params) {
    const url = `${getBaseUrl()}/api/insights/stats`;
    const request = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: JSON.stringify(params),
    };
    const response = await fetchData(url, request);
    return response.data;
}

export async function fetchFindingNames(agentId) {
    const url = `${getBaseUrl()}/api/insights/findings/names/${agentId}`;
    const request = { method: 'GET' };
    const response = await fetchData(url, request);
    return response.data;
}

export async function deleteInsightFindings(findings) {
    const url = `${getBaseUrl()}/api/insights/findings`;
    const request = {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        data: JSON.stringify({ findings })
    };
    const response = await fetchData(url, request);
    return response.data;
}

export const createDetectorTransition = async (impactDriverId, detectorData, actionData) => {
    const url = `${getBaseUrl()}/api/impact-drivers/${impactDriverId}/detector-transition`

    const request = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: JSON.stringify({
            detector: detectorData,
            action: actionData
        })
    }

    try {
        const response = await fetchData(url, request)
        if (response.statusText !== 'OK') {
            throw new Error('Server error while performing the operation.')
        }
        return response.data
    } catch (error) {
        toastr.error('Unable to create AI evaluation')
        throw error
    }
}

export const updateDetectorTransition = async (impactDriverId, actionId, data) => {
    const url = `${getBaseUrl()}/api/impact-drivers/${impactDriverId}/detector-transition/${actionId}`

    const request = {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        data: JSON.stringify(data)
    }

    try {
        const response = await fetchData(url, request)
        if (response.statusText !== 'OK') {
            throw new Error('Server error while performing the operation.')
        }
        return response.data
    } catch (error) {
        toastr.error('Unable to update AI evaluation')
        throw error
    }
}

export const deleteDetectorTransition = async (impactDriverId, actionId) => {
    const url = `${getBaseUrl()}/api/impact-drivers/${impactDriverId}/detector-transition/${actionId}`
    const request = {
        method: 'DELETE'
    }

    try {
        const response = await fetchData(url, request)
        if (response.statusText !== 'OK') {
            throw new Error('Server error while performing the operation.')
        }
        return response.data
    } catch (error) {
        toastr.error('Unable to delete AI evaluation')
        throw error
    }

    const response = await fetchData(url, request)
    return response.data
}

export async function getInsightExecutionStatus(insightId) {
    const url = `${getBaseUrl()}/api/insights/execution-status/${insightId}`
    const request = {
        method: 'GET',
        headers: { 'Content-Type': 'application/json', }
    }
    const response = await fetchData(url, request)
    return response.data
}

export async function runAIInsight(insightId, customerCRMId) {
    const url = `${getBaseUrl()}/api/insights/run/${insightId}/${customerCRMId}`
    const request = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', }
    }
    const response = await fetchData(url, request)
    return response.data
}

export async function evaluateInsight({ evaluation, insightValueId, insightItemName = null, reason = '' }) {
    const url = `${getBaseUrl()}/api/evaluate-insight/${evaluation}`;
    const body = {
        insightValueId,
        reason: reason?.trim()
    };

    if (insightItemName) {
        body.insightItem = insightItemName;
    }
    const request = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: JSON.stringify(body),
    };

    const response = await fetchData(url, request);
    return response.data;
}

export async function saveSparklineSelection(impactDriverName, period) {
    try {
        const response = await fetch('/api/column-view/sparkline', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                impactDriverName,
                period,
            }),
        });
        return await response.json();
    } catch (error) {
        console.error('Error saving sparkline selection:', error);
        throw error;
    }
};

export async function deleteSparkline(impactDriverName) {
    try {
        const response = await fetch('/api/column-view/sparkline', {
            method: 'DELETE',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                impactDriverName,
            }),
        });
        return await response.json();
    } catch (error) {
        console.error('Error deleting sparkline:', error);
        throw error;
    }
}

export const saveWidgetsState = async (widgetSettings) => {
    const url = `${getBaseUrl()}/api/settings/widgets`
    const request = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: JSON.stringify({ widgets: widgetSettings })
    }
    const response = await fetchData(url, request)

    return response.data
}

/**
 * Fetch multiple widget metrics in a single request.
 * This optimizes dashboard loading by deserializing customers only once on the backend.
 * @param {string[]} widgetIds - Array of widget IDs to fetch data for
 * @param {object} params - Widget-specific parameters (e.g., { contractsEndingSoon: { startDate, endDate } })
 * @returns {Promise<object>} - Object keyed by widget ID with their respective data
 */
export const fetchBulkWidgetMetrics = async (widgetIds, params = {}) => {
    const url = `${getBaseUrl()}/api/dashboard/bulk-metrics`
    const request = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: JSON.stringify({ widgets: widgetIds, params })
    }
    const response = await fetchData(url, request)

    return response.data
}

export const fetchDashboardPages = async () => {
    const url = `${getBaseUrl()}/api/dashboard/pages`
    const request = { method: 'GET' }
    const response = await fetchData(url, request)

    return response.data
}

export const createDashboardPage = async (name, widgets = []) => {
    const url = `${getBaseUrl()}/api/dashboard/pages`
    const request = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: JSON.stringify({ name, widgets })
    }
    const response = await fetchData(url, request)

    return response.data
}

export const updateDashboardPage = async (id, data) => {
    const url = `${getBaseUrl()}/api/dashboard/pages/${id}`
    const request = {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        data: JSON.stringify(data)
    }
    const response = await fetchData(url, request)

    return response.data
}

export const deleteDashboardPage = async (id) => {
    const url = `${getBaseUrl()}/api/dashboard/pages/${id}`
    const request = { method: 'DELETE' }
    const response = await fetchData(url, request)

    return response.data
}

export const reorderDashboardPages = async (data) => {
    const url = `${getBaseUrl()}/api/dashboard/pages/reorder`;
    const request = {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        data: JSON.stringify(data),
    };
    const response = await fetchData(url, request);
    return response.data;
}

export const isUserLoggedIn = async (privileged = false) => {
    try {
        let url = `${getBaseUrl()}/login/check`;
        if (privileged) {
            url += '?privileged=true';
        }
        const request = { method: 'GET' };
        const response = await fetchData(url, request);

        return response.data.result;
    } catch (error) {
        console.error('Error checking login status:', error);
        return false;
    }
};

export const fetchAIInsightTemplates = async () => {
    const url = `${getBaseUrl()}/api/insight-templates`
    const request = { method: 'GET' }
    const response = await fetchData(url, request)

    return response.data
}

// 
export const fetchPropertyValueForCustomer = async (propertyName, customerCRMId, timeframeDays) => {
    const url = `${getBaseUrl()}/api/dashboard/property-values/${customerCRMId}/${propertyName}/${timeframeDays}`;
    const request = { method: 'GET' };
    const response = await fetchData(url, request);
    return response.data;
}

export const addChartToSidebar = async (propertyName) => {
    const url = `${getBaseUrl()}/api/dashboard/sidebar/add-chart/${propertyName}`;
    const request = { method: 'GET' };
    const response = await fetchData(url, request);
    return response.data;
}

export const removeChartFromSidebar = async (propertyName) => {
    const url = `${getBaseUrl()}/api/dashboard/sidebar/remove-chart/${propertyName}`;
    const request = { method: 'GET' };
    const response = await fetchData(url, request);
    return response.data;
}

export const trackEvent = async (eventName) => {
    const url = `${getBaseUrl()}/track/${eventName}`;
    const request = { method: 'GET' };
    const response = await fetchData(url, request);
    return response.data;
}

export const fetchDataForChart = async (propname, timeStampFrom, timeStampTo, recordCrmId, userId) => {
    let url = `${getBaseUrl()}/api/propertydata/${propname}/${timeStampFrom}/${timeStampTo}`
    if (recordCrmId) {
        url += `?record=${recordCrmId}`
    }
    if (userId) {
        url += `?user=${userId}`
    }
    const request = { method: 'GET' }
    const response = await fetchData(url, request)
    return response.data
}

export const upsertChart = async (data) => {
    const url = `${getBaseUrl()}/api/charts`
    const request = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', },
        data: JSON.stringify(data)
    }
    const response = await fetchData(url, request)

    return response.data
}

export const getCharts = async () => {
    const url = `${getBaseUrl()}/api/charts`
    const request = { method: 'GET' }
    const response = await fetchData(url, request)

    return response.data
}

export const getChart = async (uuid) => {
    const url = `${getBaseUrl()}/api/charts/${uuid}`
    const request = { method: 'GET' }
    const response = await fetchData(url, request)

    return response.data
}

export const deleteChart = async (uuid) => {
    const url = `${getBaseUrl()}/api/charts/${uuid}`
    const request = { method: 'DELETE' }
    const response = await fetchData(url, request)

    return response.data
}

export const getEmbeddingUrl = async (uuid) => {
    const url = `${getBaseUrl()}/api/charts/${uuid}/embed`
    const request = { method: 'GET' }
    const response = await fetchData(url, request)

    return response.data
}

export const fetchFrontpageData = async () => {
    const url = `${getBaseUrl()}/api/frontpage-data`
    const request = { method: 'GET' }
    const response = await fetchData(url, request)

    return response.data
}

export const fetchAPBulkProgress = async () => {
    const url = `${getBaseUrl()}/api/account-plans/bulk-progress`
    const request = { method: 'GET' }
    const response = await fetchData(url, request)

    return response.data
}

export const fetchUsers = async () => {
    const url = `${getBaseUrl()}/api/users`
    const request = { method: 'GET' }
    const response = await fetchData(url, request)

    return response.data
}

export async function fetchShortInsightsList() {
    const url = `${getBaseUrl()}/api/insights`
    const request = { method: 'GET' }
    const response = await fetchData(url, request)
    return response.data
}

export async function fetchTimeline(customerCrmId, offset = 0, limit = 50, filters = {}) {
    const params = new URLSearchParams();
    params.append('limit', limit);
    params.append('offset', offset);
    if (filters.startDate) params.append('startDate', filters.startDate);
    if (filters.endDate) params.append('endDate', filters.endDate);

    if (filters.types) {
        if (filters.types.includes('drivers') && !filters.types.includes('signals')) {
            params.append('onlyDrivers', 'true');
        } else if (filters.types.includes('signals') && !filters.types.includes('drivers')) {
            params.append('onlySignals', 'true');
        }
    }

    if (filters.selectedDriver?.length) params.append('drivers', filters.selectedDriver.join(','));
    if (filters.selectedCategory?.length) params.append('categories', filters.selectedCategory.join(','));
    if (filters.selectedToStatus?.length) params.append('toStatuses', filters.selectedToStatus.join(','));
    if (filters.selectedSignalStatus?.length) params.append('signalStatuses', filters.selectedSignalStatus.join(','));

    const url = `${getBaseUrl()}/api/customer/${customerCrmId}/timeline?${params.toString()}`;
    const request = { method: 'GET' };
    const response = await fetchData(url, request);
    return response.data;
}

export const uninstallSlackIntegration = async () => {
    const url = `${getBaseUrl()}/settings/slack/uninstall`
    const request = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', }
    }
    const response = await fetchData(url, request)
    return response.data
}

export const fetchHubspotOwners = async () => {
    const url = `${getBaseUrl()}/settings/hubspot-owners`;
    const request = { method: 'GET' };
    const response = await fetchData(url, request);
    return response.data;
}

export const inviteUsers = async (users) => {
    const url = `${getBaseUrl()}/settings/users/invite`;
    const request = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: JSON.stringify({ users })
    };
    const response = await fetchData(url, request);
    return response.data;
}
export const fetchSignalsToken = async () => {
    const url = `${getBaseUrl()}/api/signals/token`
    const request = { method: 'GET' }
    const response = await fetchData(url, request)

    return response.data
}

export const fetchDetectors = async () => {
    const url = `${getBaseUrl()}/api/detectors`
    const request = { method: 'GET' }
    const response = await fetchData(url, request)

    if (!response) {
        throw new Error('Failed to fetch detectors - please log in again')
    }
    return response.data
}

export const fetchActionTemplates = async () => {
    const url = `${getBaseUrl()}/api/action-templates`
    const request = { method: 'GET' }
    const response = await fetchData(url, request)

    return response.data
}

export const updateDetectorAction = async (detectorId, actionId, data) => {
    const url = `${getBaseUrl()}/api/detectors/${detectorId}/actions/${actionId}`
    const request = {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        data: JSON.stringify(data)
    }
    const response = await fetchData(url, request)
    return response.data
}

export const createDetectorAction = async (detectorId, data) => {
    const url = `${getBaseUrl()}/api/detectors/${detectorId}/actions`
    const request = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: JSON.stringify(data)
    }
    const response = await fetchData(url, request)
    return response.data
}

export const fetchAIInsights = async () => {
    const url = `${getBaseUrl()}/api/ai-insights`
    const request = { method: 'GET' }
    const response = await fetchData(url, request)
    return response.data
}

export const fetchDetectorActions = async (detectorId) => {
    const url = `${getBaseUrl()}/api/detectors/${detectorId}/actions`
    const request = { method: 'GET' }
    const response = await fetchData(url, request)
    return response.data
}

export const deleteDetectorAction = async (detectorId, actionId) => {
    const url = `${getBaseUrl()}/api/detectors/${detectorId}/actions/${actionId}`
    const request = { method: 'DELETE' }
    const response = await fetchData(url, request)
    return response.data
}

export const saveDetectorActionsBatch = async (detectorId, actions) => {
    const url = `${getBaseUrl()}/api/detectors/${detectorId}/actions/batch`
    const request = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: JSON.stringify({ actions }),
    }
    const response = await fetchData(url, request)
    return response.data
}

export const fetchDealPipelines = async () => {
    const url = `${getBaseUrl()}/api/hubspot/deal-pipelines`
    const request = { method: 'GET' }
    const response = await fetchData(url, request)
    return response.data
}

export const fetchDealProperties = async () => {
    const url = `${getBaseUrl()}/api/hubspot/deal-properties`
    const request = { method: 'GET' }
    const response = await fetchData(url, request)
    return response.data
}

export const fetchSlackChannels = async () => {
    const url = `${getBaseUrl()}/api/slack/channels`
    const request = { method: 'GET' }
    const response = await fetchData(url, request)
    return response.data
}

export const fetchSlackUsers = async () => {
    const url = `${getBaseUrl()}/api/slack/users`
    const request = { method: 'GET' }
    const response = await fetchData(url, request)
    return response.data
}

export const updateDetector = async (id, data) => {
    const url = `${getBaseUrl()}/api/detectors/${id}`
    const request = {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        data: JSON.stringify(data)
    }
    const response = await fetchData(url, request)
    return response.data
}

export const createDetector = async (data) => {
    const url = `${getBaseUrl()}/api/detectors`
    const request = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: JSON.stringify(data)
    }
    const response = await fetchData(url, request)
    return response.data
}

export const deleteDetector = async (id) => {
    const url = `${getBaseUrl()}/api/detectors/${id}`
    const request = { method: 'DELETE' }
    const response = await fetchData(url, request)
    return response.data
}

export async function fetchSignalDetectors() {
    const url = `${getBaseUrl()}/api/signals/detectors`
    const request = { method: 'GET' }
    const response = await fetchData(url, request)
    return response.data
}

export async function fetchSignals(params) {
    const url = `${getBaseUrl()}/api/signals/list?${params.toString()}`
    const request = { method: 'GET' }
    const response = await fetchData(url, request)
    return response.data
}

export async function fetchSignalStats(params) {
    const url = `${getBaseUrl()}/api/signals/stats?${params.toString()}`
    const request = { method: 'GET' }
    const response = await fetchData(url, request)
    return response.data
}

// Takes the same URLSearchParams shape as fetchSignals().
export async function fetchActivityReminders(params) {
    const url = `${getBaseUrl()}/api/signals/reminders?${params.toString()}`
    const request = { method: 'GET' }
    const response = await fetchData(url, request)
    return response.data
}

export async function fetchSignalTabCounts(params) {
    const url = `${getBaseUrl()}/api/signals/counts?${params.toString()}`
    const request = { method: 'GET' }
    const response = await fetchData(url, request)
    return response.data
}

// Fetch a single signal by its public UUID (used to open a shared deep-link
// for a signal that isn't in the current feed). Returns null on 404.
export async function fetchSignalByPublicId(publicId) {
    const url = `${getBaseUrl()}/api/signals/${encodeURIComponent(publicId)}`
    const request = { method: 'GET' }
    try {
        const response = await fetchData(url, request)
        return response.data?.signal || null
    } catch (e) {
        return null
    }
}

/**
 * Get prefilled form data for a recommended action on a signal.
 * For AI-generated types, returns existing draft from a STATUS_SUGGESTED row or generates one.
 * For task/deal types, returns computed defaults.
 */
export const prefillSignalAction = async (signalId, actionId) => {
    const url = `${getBaseUrl()}/api/signals/${signalId}/actions/${actionId}/prefill`
    const request = { method: 'GET' }
    const response = await fetchData(url, request)
    return response.data
}

/**
 * Refine an AI-generated draft via a follow-up prompt.
 */
export const refineSignalAction = async (signalId, actionId, payload) => {
    const url = `${getBaseUrl()}/api/signals/${signalId}/actions/${actionId}/refine`
    const request = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: JSON.stringify(payload),
    }
    const response = await fetchData(url, request)
    return response.data
}

/**
 * Execute a recommended action on a signal. Payload: { formData, resolveSignal }.
 */
export const executeSignalAction = async (signalId, actionId, payload) => {
    const url = `${getBaseUrl()}/api/signals/${signalId}/actions/${actionId}/execute`
    const request = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: JSON.stringify(payload),
    }
    const response = await fetchData(url, request)
    return response.data
}

/**
 * Generate/refine AI content for an "Other actions" template.
 * Payload: { currentText?, refinementInstruction? }
 */
export const generateOtherSignalAction = async (signalId, templateId, payload) => {
    const url = `${getBaseUrl()}/api/signals/${signalId}/other-actions/${templateId}/generate`
    const request = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: JSON.stringify(payload),
    }
    const response = await fetchData(url, request)
    return response.data
}

/**
 * Execute an "Other actions" entry: a template not configured for the signal's detector.
 */
export const executeOtherSignalAction = async (signalId, templateId, payload) => {
    const url = `${getBaseUrl()}/api/signals/${signalId}/other-actions/${templateId}/execute`
    const request = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: JSON.stringify(payload),
    }
    const response = await fetchData(url, request)
    return response.data
}

/**
 * Resolve a signal (positive evaluation). Removes from active list.
 */
export const resolveSignal = async (signalId, note) => {
    const url = `${getBaseUrl()}/api/signals/${signalId}/resolve`
    const request = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: JSON.stringify({ note }),
    }
    const response = await fetchData(url, request)
    return response.data
}

/**
 * Record that the account owner opened a signal or activity reminder. A no-op
 * for anyone else. `source` says where it was opened from: click or slack.
 */
export const markSignalSeen = async (signalId, source = 'click') => {
    const url = `${getBaseUrl()}/api/signals/${signalId}/seen`
    const request = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: JSON.stringify({ source }),
    }
    const response = await fetchData(url, request)
    return response.data
}

/**
 * "Mark as unseen": the owner's reminder to come back to a signal.
 */
export const markSignalUnseen = async (signalId) => {
    const url = `${getBaseUrl()}/api/signals/${signalId}/unseen`
    const response = await fetchData(url, { method: 'POST' })
    return response.data
}

/**
 * Remove signal from feed (without resolving it).
 */
export const removeSignalFromFeed = async (signalId) => {
    const url = `${getBaseUrl()}/api/signals/${signalId}/remove-from-feed`
    const request = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
    }
    const response = await fetchData(url, request)
    return response.data
}

/**
 * Put a removed signal back in the feed (undo removeSignalFromFeed).
 */
export const restoreSignalToFeed = async (signalId) => {
    const url = `${getBaseUrl()}/api/signals/${signalId}/restore-to-feed`
    const request = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
    }
    const response = await fetchData(url, request)
    return response.data
}

/**
 * Mark a signal as incorrect (negative evaluation) with a reason.
 */
export const markSignalIncorrect = async (signalId, reason) => {
    const url = `${getBaseUrl()}/api/signals/${signalId}/mark-incorrect`
    const request = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: JSON.stringify({ reason }),
    }
    const response = await fetchData(url, request)
    return response.data
}

/**
 * Fetch CRM owners (users that can be assigned to tasks/deals).
 * Returns [{id, label, email}].
 */
export const fetchCrmOwners = async () => {
    const url = `${getBaseUrl()}/api/values/crm-owners`
    const request = { method: 'GET' }
    const response = await fetchData(url, request)
    return response.data
}

// Detector Templates API
export const fetchDetectorTemplates = async () => {
    const url = `${getBaseUrl()}/api/detector-templates`
    const request = { method: 'GET' }
    const response = await fetchData(url, request)
    return response.data
}

// Engagement Tags API
export const fetchEngagementTags = async () => {
    const url = `${getBaseUrl()}/api/engagement-tags`
    const request = { method: 'GET' }
    const response = await fetchData(url, request)
    return response.data
}

export const createDetectorFromTemplate = async (templateId, parameters, name, triggerMode = 'on_change') => {
    const url = `${getBaseUrl()}/api/detectors/from-template`
    const request = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: JSON.stringify({
            templateId,
            parameters,
            name,
            triggerMode
        })
    }
    const response = await fetchData(url, request)
    return response.data
}

export const generateSignalPrompt = async (payload) => {
    const url = `${getBaseUrl()}/api/signal-settings/generate-prompt`
    const request = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: JSON.stringify(payload)
    }
    const response = await fetchData(url, request)
    return response.data
}


export const parseNaturalLanguageDetector = async (query) => {
    const url = `${getBaseUrl()}/api/detectors/parse-natural-language`
    const request = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: JSON.stringify({ query })
    }
    const response = await fetchData(url, request)
    return response.data
}


export const fetchCategories = async (includeFilters = false) => {
    const url = includeFilters ? `${getBaseUrl()}/api/filter-categories?includeFilters=1` : `${getBaseUrl()}/api/filter-categories`;
    const request = {
        method: 'GET',
    };
    const response = await fetchData(url, request);
    return response.data;
}

export const createCategory = async (data) => {
    const url = `${getBaseUrl()}/api/filter-categories`;
    const request = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: JSON.stringify(data)
    };
    const response = await fetchData(url, request);
    return response.data;
}

export const updateCategory = async (id,data) => {
    const url = `${getBaseUrl()}/api/filter-category/${id}`;
    const request = {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        data: JSON.stringify(data)
    };
    const response = await fetchData(url, request);
    return response.data;
}

export const reorderCategory = async (data) => {
    const url = `${getBaseUrl()}/api/filter-categories/reorder`;
    const request = {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        data: JSON.stringify(data)
    };
    const response = await fetchData(url, request);
    return response.data;
}

export const deleteCategory = async (id) => {
    const url = `${getBaseUrl()}/api/filter-category/${id}`
    const request = {
        method: 'DELETE',
    }
    const response = await fetchData(url, request)
    return response.data
}

export const reorderFilters = async (data) => {
    const url = `${getBaseUrl()}/api/filters/reorder`;
    const request = {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        data: JSON.stringify(data)
    };
    const response = await fetchData(url, request);
    return response.data;
}

export const setDefaultSegment = async (filterId) => {
    const url = `${getBaseUrl()}/api/filters/set-default`;
    const request = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: JSON.stringify({ filterId }),
    };
    const response = await fetchData(url, request);
    return response.data;
}

export const  fetchPrompts = async () => {
    const url =`${getBaseUrl()}/api/chat/prompts`;
    const request = {
        method: 'GET',
    };
    const response = await fetchData(url, request);
    return response.data;
}

export const  createPrompt = async (data) => {
    const url =`${getBaseUrl()}/api/chat/prompts`;
    const request = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: JSON.stringify(data)
    };
    const response = await fetchData(url, request);
    return response.data;
}

export const  updatePrompt = async (data, id) => {
    const url =`${getBaseUrl()}/api/chat/prompts/${id}`;
    const request = {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        data: JSON.stringify(data)
    };
    const response = await fetchData(url, request);
    return response.data;
}

export const deletePrompt = async (id) => {
    const url = `${getBaseUrl()}/api/chat/prompts/${id}`
    const request = {
        method: 'DELETE',
    }
    const response = await fetchData(url, request)
    return response.data
}

// Signal Settings API
export const fetchSignalSettings = async () => {
    const url = `${getBaseUrl()}/api/signal-settings`
    const request = { method: 'GET' }
    const response = await fetchData(url, request)
    return response.data
}

export const fetchSignalSetting = async (id) => {
    const url = `${getBaseUrl()}/api/signal-settings/${id}`
    const request = { method: 'GET' }
    const response = await fetchData(url, request)
    return response.data
}

export const createSignalSetting = async (data) => {
    const url = `${getBaseUrl()}/api/signal-settings`
    const request = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: JSON.stringify(data)
    }
    const response = await fetchData(url, request)
    return response.data
}

export const updateSignalSetting = async (id, data) => {
    const url = `${getBaseUrl()}/api/signal-settings/${id}`
    const request = {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        data: JSON.stringify(data)
    }
    const response = await fetchData(url, request)
    return response.data
}

export const deleteSignalSetting = async (id) => {
    const url = `${getBaseUrl()}/api/signal-settings/${id}`
    const request = { method: 'DELETE' }
    const response = await fetchData(url, request)
    return response.data
}

export const toggleSignalSetting = async (id, enabled) => {
    const url = `${getBaseUrl()}/api/signal-settings/${id}/toggle`
    const request = {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        data: JSON.stringify({ enabled })
    }
    const response = await fetchData(url, request)
    return response.data
}

export const fetchSignalCheckLogs = async (signalId, { dateFrom, dateTo, offset = 0, limit = 20 } = {}) => {
    const params = new URLSearchParams()
    if (dateFrom) params.append('dateFrom', dateFrom)
    if (dateTo) params.append('dateTo', dateTo)
    params.append('offset', String(offset))
    params.append('limit', String(limit))
    const url = `${getBaseUrl()}/api/signal-settings/${signalId}/check-logs?${params.toString()}`
    const request = { method: 'GET' }
    const response = await fetchData(url, request)
    return response.data
}

export const fetchDetectedSignals = async (signalId, { dateFrom, dateTo, statuses = [], actionedFilter = null, offset = 0, limit = 20 } = {}) => {
    const params = new URLSearchParams()
    if (dateFrom) params.append('dateFrom', dateFrom)
    if (dateTo) params.append('dateTo', dateTo)
    statuses.forEach((status) => params.append('statuses[]', status))
    if (actionedFilter) params.append('actionedFilter', actionedFilter)
    params.append('offset', String(offset))
    params.append('limit', String(limit))
    const url = `${getBaseUrl()}/api/signal-settings/${signalId}/detected?${params.toString()}`
    const request = { method: 'GET' }
    const response = await fetchData(url, request)
    return response.data
}

export const deleteDetectedSignals = async (signalId, signalIds) => {
    const url = `${getBaseUrl()}/api/signal-settings/${signalId}/detected`
    const request = {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        data: JSON.stringify({ signalIds }),
    }
    const response = await fetchData(url, request)
    return response.data
}

export const fetchSignalFindings = async ({ signalIds = [], statuses = [], actionedFilter = null, dateFrom, dateTo, offset = 0, limit = 20, sortOrder = 'DESC' } = {}) => {
    const params = new URLSearchParams()
    signalIds.forEach((id) => params.append('signalIds[]', id))
    statuses.forEach((status) => params.append('statuses[]', status))
    if (actionedFilter) params.append('actionedFilter', actionedFilter)
    if (dateFrom) params.append('dateFrom', dateFrom)
    if (dateTo) params.append('dateTo', dateTo)
    params.append('offset', String(offset))
    params.append('limit', String(limit))
    params.append('sortOrder', sortOrder)
    const url = `${getBaseUrl()}/api/signal-settings/findings?${params.toString()}`
    const request = { method: 'GET' }
    const response = await fetchData(url, request)
    return response.data
}

export const deleteSignalFindings = async (signalIds) => {
    const url = `${getBaseUrl()}/api/signal-settings/findings`
    const request = {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        data: JSON.stringify({ signalIds }),
    }
    const response = await fetchData(url, request)
    return response.data
}

export const installLibrarySignal = async (libraryKey, enabled = true) => {
    const url = `${getBaseUrl()}/api/signal-settings/library/${encodeURIComponent(libraryKey)}/install`
    const request = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: JSON.stringify({ enabled })
    }
    const response = await fetchData(url, request)
    return response.data
}

export const dismissLibrarySignal = async (libraryKey) => {
    const url = `${getBaseUrl()}/api/signal-settings/library/${encodeURIComponent(libraryKey)}`
    const request = { method: 'DELETE' }
    const response = await fetchData(url, request)
    return response.data
}

export const fetchFirstSignalRunStatus = async () => {
    const url = `${getBaseUrl()}/api/signal-settings/first-run-status`
    const request = { method: 'GET' }
    const response = await fetchData(url, request)
    return response.data
}

export const fetchActivities = async () => {
    const url = `${getBaseUrl()}/api/activities`
    const request = { method: 'GET' }
    const response = await fetchData(url, request)
    return response.data
}

export const createActivity = async (data) => {
    const url = `${getBaseUrl()}/api/activities`
    const request = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: JSON.stringify(data)
    }
    const response = await fetchData(url, request)
    return response.data
}

export const updateActivity = async (id, data) => {
    const url = `${getBaseUrl()}/api/activities/${id}`
    const request = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: JSON.stringify(data)
    }
    const response = await fetchData(url, request)
    return response.data
}

export const deleteActivity = async (id) => {
    const url = `${getBaseUrl()}/api/activities/${id}`
    const request = { method: 'DELETE' }
    const response = await fetchData(url, request)
    return response.data
}

export const fetchProcesses = async () => {
    const url = `${getBaseUrl()}/api/processes`
    const request = { method: 'GET' }
    const response = await fetchData(url, request)
    return response.data
}

export const fetchProcessesSummary = async () => {
    const url = `${getBaseUrl()}/api/processes/summary`
    const request = { method: 'GET' }
    const response = await fetchData(url, request)
    return response.data
}

export const fetchProcess = async (id) => {
    const url = `${getBaseUrl()}/api/processes/${id}`
    const request = { method: 'GET' }
    const response = await fetchData(url, request)
    return response.data
}

export const createProcess = async (data) => {
    const url = `${getBaseUrl()}/api/processes`
    const request = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: JSON.stringify(data)
    }
    const response = await fetchData(url, request)
    return response.data
}

export const updateProcess = async (id, data) => {
    const url = `${getBaseUrl()}/api/processes/${id}`
    const request = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: JSON.stringify(data)
    }
    const response = await fetchData(url, request)
    return response.data
}

export const deleteProcess = async (id) => {
    const url = `${getBaseUrl()}/api/processes/${id}`
    const request = { method: 'DELETE' }
    const response = await fetchData(url, request)
    return response.data
}

export const fetchProcessTemplates = async () => {
    const url = `${getBaseUrl()}/api/process-templates`
    const request = { method: 'GET' }
    const response = await fetchData(url, request)
    return response.data
}

export const publishProcess = async (id) => {
    const url = `${getBaseUrl()}/api/processes/${id}/publish`
    const request = { method: 'POST' }
    const response = await fetchData(url, request)
    return response.data
}

export const fetchProcessPublishStatus = async () => {
    const url = `${getBaseUrl()}/api/processes/publish-status`
    const request = { method: 'GET' }
    const response = await fetchData(url, request)
    return response.data
}

export const fetchProcessCycleGrid = async ({ processId = null, customerId = null } = {}) => {
    const params = new URLSearchParams()
    if (processId) params.append('processId', processId)
    if (customerId) params.append('customerId', customerId)
    const query = params.toString()
    const url = `${getBaseUrl()}/api/process-cycles/grid${query ? `?${query}` : ''}`
    const request = { method: 'GET' }
    const response = await fetchData(url, request)
    return response.data
}

export const fetchCompletions = async (params) => {
    const url = `${getBaseUrl()}/api/process-cycles/completions?${params.toString()}`
    const request = { method: 'GET' }
    const response = await fetchData(url, request)
    return response.data
}

export const fetchProcessStatuses = async () => {
    const url = `${getBaseUrl()}/api/process-statuses`
    const request = { method: 'GET' }
    const response = await fetchData(url, request)
    return response.data
}

export const setActivityStatus = async (cycleId, data) => {
    const url = `${getBaseUrl()}/api/process-cycles/${cycleId}/status-changes`
    const request = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: JSON.stringify(data)
    }
    const response = await fetchData(url, request)
    return response.data
}

export const fetchActivityHistory = async (cycleId, customerId, activityId) => {
    const params = new URLSearchParams({ customerId, activityId })
    const url = `${getBaseUrl()}/api/process-cycles/${cycleId}/history?${params.toString()}`
    const request = { method: 'GET' }
    const response = await fetchData(url, request)
    return response.data
}

export const fetchActivityActions = async (cycleId, customerId, activityId) => {
    const params = new URLSearchParams({ customerId, activityId })
    const url = `${getBaseUrl()}/api/process-cycles/${cycleId}/activity-actions?${params.toString()}`
    const request = { method: 'GET' }
    const response = await fetchData(url, request)
    return response.data
}

export const prefillActivityAction = async (cycleId, actionId, payload) => {
    const url = `${getBaseUrl()}/api/process-cycles/${cycleId}/activity-actions/${actionId}/prefill`
    const request = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: JSON.stringify(payload)
    }
    const response = await fetchData(url, request)
    return response.data
}

export const executeActivityAction = async (cycleId, actionId, payload) => {
    const url = `${getBaseUrl()}/api/process-cycles/${cycleId}/activity-actions/${actionId}/execute`
    const request = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: JSON.stringify(payload)
    }
    const response = await fetchData(url, request)
    return response.data
}

/**
 * Rewrite the draft of an activity action.
 * Payload: { customerId, periodIndex, currentText, refinementInstruction }.
 */
export const refineActivityAction = async (cycleId, actionId, payload) => {
    const url = `${getBaseUrl()}/api/process-cycles/${cycleId}/activity-actions/${actionId}/refine`
    const request = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: JSON.stringify(payload)
    }
    const response = await fetchData(url, request)
    return response.data
}

/**
 * Draft an "Other actions" template — one the activity has no action for.
 * Payload: { customerId, activityId, periodIndex }.
 */
export const prefillOtherActivityAction = async (cycleId, templateId, payload) => {
    const url = `${getBaseUrl()}/api/process-cycles/${cycleId}/activity-actions/other/${templateId}/prefill`
    const request = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: JSON.stringify(payload)
    }
    const response = await fetchData(url, request)
    return response.data
}

/**
 * Rewrite the draft of an "Other actions" template.
 * Payload: { customerId, activityId, periodIndex, currentText, refinementInstruction }.
 */
export const refineOtherActivityAction = async (cycleId, templateId, payload) => {
    const url = `${getBaseUrl()}/api/process-cycles/${cycleId}/activity-actions/other/${templateId}/refine`
    const request = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: JSON.stringify(payload)
    }
    const response = await fetchData(url, request)
    return response.data
}

/**
 * Run an "Other actions" template against one occurrence of the activity.
 */
export const executeOtherActivityAction = async (cycleId, templateId, payload) => {
    const url = `${getBaseUrl()}/api/process-cycles/${cycleId}/activity-actions/other/${templateId}/execute`
    const request = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: JSON.stringify(payload)
    }
    const response = await fetchData(url, request)
    return response.data
}

export const fetchActivitySuggestions = async () => {
    const url = `${getBaseUrl()}/api/ts/activity-suggestions`
    const request = { method: 'GET' }
    const response = await fetchData(url, request)
    return response.data
}
